<template>
  <div class="campaign-dashboard flex flex-col flex-1">
    <!-- Notification Banner -->
    <div
      v-if="notification.message"
      :class="['fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-white text-sm transition-opacity duration-300', notification.type === 'success' ? 'bg-green-600' : 'bg-red-600']"
    >
      {{ notification.message }}
    </div>

    <!-- Loading / Error State -->
    <div v-if="loading" class="flex justify-center items-center py-8">
      <img src="../../images/icons/loading.png" class="w-6 h-6 ml-2 icon loading-icon"/>
    </div>
    <div v-else-if="error" class="flex justify-center items-center py-8">
      <div class="text-red-500">
        {{ error }}
        <button @click="fetchDashboardData" class="ml-2 px-3 py-1 btn btn-primary text-white rounded">
          {{ $t('campaigns.dashboard.retry') }}
        </button>
      </div>
    </div>

    <div v-else class="flex flex-col gap-4">
      <!-- Campaign Info & Progress -->
      <div class="bg-gray-600 rounded-8px p-4">
        <div class="flex justify-between items-center">
          <h2 class="text-xl font-semibold text-white">{{ dashboardData.campaign_info.title }}</h2>
          <div :class="['px-3 py-1 rounded-full text-xs font-medium', campaignStateClass]">
            {{ $t(`campaigns.dashboard.state.${dashboardData.campaign_info.state}`) }}
          </div>
        </div>
        <div class="flex flex-col sm:flex-row justify-between items-center sm:items-center mb-4">
          <div class="text-sm text-grey-1">
            {{ $t('campaigns.dashboard.campaign_period') }}:
            {{ formatDate(dashboardData.campaign_info.start_at) }} ~ {{ formatDate(dashboardData.campaign_info.end_at) }}
          </div>
          <!-- Days Left -->
          <div class="text-sm text-grey-1">
            <span v-if="dashboardData.campaign_info.days_left > 0">
              {{ $t('campaigns.dashboard.days_left', { count: dashboardData.campaign_info.days_left }) }}
            </span>
            <span v-else-if="dashboardData.campaign_info.days_left === 0">
              {{ $t('campaigns.dashboard.ends_today') }}
            </span>
            <span v-else>
              {{ $t('campaigns.dashboard.ended') }}
            </span>
          </div>
        </div>

        <!-- Progress Bar -->
        <div class="progress-bar">
          <div class="flex justify-between text-sm mb-1 text-white">
            <span>{{ $t('campaigns.dashboard.current_amount') }}: ${{ formatNumber(dashboardData.campaign_info.current_amount) }}</span>
            <span>{{ $t('campaigns.dashboard.goal_price') }}: ${{ formatNumber(dashboardData.campaign_info.goal_price) }}</span>
          </div>
          <div class="w-full bg-grey-3 rounded-full h-2.5">
            <div
              class="bg-primary h-2.5 rounded-full transition-all duration-500"
              :style="{ width: `${Math.min(100, dashboardData.campaign_info.progress_percentage)}%` }"
            ></div>
          </div>
          <div class="flex justify-between text-xs text-grey-1 mt-1">
            <span>{{ dashboardData.campaign_info.progress_percentage.toFixed(1) }}%</span>
            <span>{{ $t('campaigns.dashboard.backers_count') }}: {{ dashboardData.campaign_info.backers_count }}</span>
          </div>
        </div>
      </div>

      <!-- Date Range Picker -->
      <div class="search-bar flex">
        <div class="flex flex-col sm:flex-row w-full justify-end">
          <div class="flex items-center">
            <date-range-picker
              v-model="calendarDateRange"
              :placeholder="$t('campaigns.dashboard.select_date_range')"
              ref="calendarRef"
              calendar-id="campaign-calendar"
              @visibility-change="isCalendarVisible = $event"
            />
          </div>
        </div>
      </div>

      <!-- Sales Summary Cards -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div class="bg-gray-600 rounded-8px p-4">
          <p class="text-sm text-grey-1">{{ $t('campaigns.dashboard.total_sales_amount') }}</p>
          <p class="text-2xl font-bold mt-1" style="color: #2BDEE9;">${{ formatNumber(dashboardData.sales_summary.total_sales_amount) }}</p>
        </div>
        <div class="bg-gray-600 rounded-8px p-4">
          <p class="text-sm text-grey-1">{{ $t('campaigns.dashboard.total_units_sold') }}</p>
          <p class="text-2xl font-bold mt-1" style="color: #DC49D6;">{{ dashboardData.sales_summary.total_units_sold }}</p>
        </div>
        <div class="bg-gray-600 rounded-8px p-4">
          <p class="text-sm text-grey-1">{{ $t('campaigns.dashboard.total_orders') }}</p>
          <p class="text-2xl font-bold mt-1" style="color: #FFCB68;">{{ dashboardData.sales_summary.total_orders }}</p>
        </div>
        <div class="bg-gray-600 rounded-8px p-4">
          <p class="text-sm text-grey-1">{{ $t('campaigns.dashboard.average_order_value') }}</p>
          <p class="text-2xl font-bold mt-1" style="color: #29CB00;">${{ formatNumber(dashboardData.sales_summary.average_order_value) }}</p>
        </div>
      </div>

      <!-- Sales Trend Chart -->
      <div class="bg-gray-600 rounded-8px p-4">
        <h2 class="text-lg font-semibold mb-4 text-white">{{ $t('campaigns.dashboard.sales_trend') }}</h2>
        <CampaignDashboardChart
          :chart-data="dashboardData.chart_data"
          :start-at="dateRange.start"
          :end-at="dateRange.end"
        />
      </div>

      <!-- Export Orders Button -->
      <div class="bg-gray-600 rounded-8px p-4">
        <h2 class="text-lg font-semibold mb-2 text-white">{{ $t('campaigns.dashboard.export_title') }}</h2>
        <p class="text-sm text-grey-1 mb-4">{{ $t('campaigns.dashboard.export_description') }}</p>
        <button
          @click="handleExportOrders"
          :disabled="exportLoading"
          class="btn btn-primary flex items-center gap-2"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span>{{ $t('campaigns.dashboard.export_button') }}</span>
          <img v-if="exportLoading" src="../../images/icons/loading.png" class="w-4 h-4 ml-2 animate-spin opacity-80"/>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { format, parse } from 'date-fns'
import debounce from 'lodash/debounce'

export default {
  name: 'CampaignDashboard',
  props: {
    campaignId: {
      type: Number,
      required: true
    }
  },
  setup(props) {
    const loading = ref(true)
    const error = ref(null)
    const exportLoading = ref(false)
    const notification = ref({ message: '', type: '' })

    const showNotification = (message, type = 'success') => {
      notification.value = { message, type }
      setTimeout(() => { notification.value = { message: '', type: '' } }, 3000)
    }
    const dashboardData = ref({
      campaign_info: {},
      sales_summary: {},
      chart_data: { daily_sales_amount: {}, daily_sales_count: {} }
    })
    const dateRange = ref({ start: '', end: '' })

    // Calendar refs (same pattern as analytics)
    const calendarRef = ref(null)
    const isCalendarVisible = ref(false)
    const scrollListener = ref(null)

    // v-model computed for DateRangePicker (same pattern as ProfileAnalyticsSearchForm)
    const calendarDateRange = computed({
      get: () => ({
        start: dateRange.value.start ? dateRange.value.start.replace(/\//g, '-') : '',
        end: dateRange.value.end ? dateRange.value.end.replace(/\//g, '-') : ''
      }),
      set: (value) => {
        if (value.start && value.end) {
          const newStart = format(parse(value.start, 'yyyy-MM-dd', new Date()), 'yyyy/MM/dd')
          const newEnd = format(parse(value.end, 'yyyy-MM-dd', new Date()), 'yyyy/MM/dd')

          const currentStart = dateRange.value.start
          const currentEnd = dateRange.value.end

          if (newStart !== currentStart || newEnd !== currentEnd) {
            dateRange.value = { start: newStart, end: newEnd }
            nextTick(() => {
              fetchDashboardData()
            })
          }
        }
      }
    })

    const fetchDashboardData = async () => {
      loading.value = true
      error.value = null
      try {
        const params = new URLSearchParams()
        if (dateRange.value.start) params.append('start_at', dateRange.value.start)
        if (dateRange.value.end) params.append('end_at', dateRange.value.end)

        const response = await fetch(`/api/v1/profile/campaigns/${props.campaignId}/dashboard?${params.toString()}`)
        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.error || 'Failed to load dashboard data')
        }
        const data = await response.json()
        dashboardData.value = data

        // Set initial date range from campaign info
        if (!dateRange.value.start && data.campaign_info.start_at) {
          dateRange.value.start = format(new Date(data.campaign_info.start_at), 'yyyy/MM/dd')
        }
        if (!dateRange.value.end) {
          dateRange.value.end = format(new Date(), 'yyyy/MM/dd')
        }
      } catch (e) {
        console.error('Error fetching campaign dashboard data:', e)
        error.value = e.message
      } finally {
        loading.value = false
      }
    }

    const formatDate = (isoString) => {
      if (!isoString) return ''
      const date = new Date(isoString)
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    }

    const formatNumber = (num) => {
      if (num == null) return '0.00'
      return Number(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    }

    const handleExportOrders = async () => {
      exportLoading.value = true
      try {
        const params = new URLSearchParams()
        if (dateRange.value.start) params.append('start_at', dateRange.value.start)
        if (dateRange.value.end) params.append('end_at', dateRange.value.end)

        const response = await fetch(
          `/api/v1/profile/campaigns/${props.campaignId}/export_orders?${params}`,
          {
            method: 'POST',
            headers: {
              'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
              'Content-Type': 'application/json'
            }
          }
        )

        if (response.ok) {
          const data = await response.json()
          showNotification(data.message || 'Export started. You will receive an email when ready.')
        } else {
          const errorData = await response.json()
          throw new Error(errorData.error || 'Export failed')
        }
      } catch (e) {
        console.error('Error exporting orders:', e)
        showNotification(e.message || 'Export failed. Please try again.', 'error')
      } finally {
        exportLoading.value = false
      }
    }

    // Scroll-to-hide calendar (same as analytics)
    const handleScroll = () => {
      if (calendarRef.value && isCalendarVisible.value) {
        calendarRef.value.hide()
      }
    }

    const debouncedHandleScroll = debounce(handleScroll, 100)

    onMounted(() => {
      fetchDashboardData()
      scrollListener.value = debouncedHandleScroll
      window.addEventListener('scroll', scrollListener.value, { passive: true })
    })

    onUnmounted(() => {
      if (scrollListener.value) {
        window.removeEventListener('scroll', scrollListener.value)
        scrollListener.value = null
      }
    })

    return {
      loading,
      error,
      exportLoading,
      notification,
      dashboardData,
      dateRange,
      calendarDateRange,
      calendarRef,
      isCalendarVisible,
      fetchDashboardData,
      formatDate,
      formatNumber,
      handleExportOrders
    }
  },
  computed: {
    campaignStateClass() {
      const state = this.dashboardData.campaign_info.state
      switch (state) {
        case 'published': return 'bg-green-600 text-white'
        case 'pending': return 'bg-yellow-600 text-white'
        case 'draft': return 'bg-gray-500 text-white'
        case 'discarded': return 'bg-red-600 text-white'
        default: return 'bg-gray-500 text-white'
      }
    }
  }
}
</script>
